import { useQuery } from '@tanstack/react-query';
import { getAllCreationRequests } from '../services/creationRequestService';
import { getUserCount } from '../services/userService';
import { getTicketsCountByStatus, getAllTickets, TICKET_STATUS } from '../services/supportService';
import { getLatestClients, getSiteDashboardData } from '../services/dashboardService';

const DASHBOARD_KEY = 'dashboard';

// Statuts « ouverts » d'une demande (hors brouillon, hors suivi terminé)
const ACTIVE_REQUEST_STATUSES = ['nouvelle', 'creation_en_cours', 'verification', 'site_pret'];

const toTime = (value) => (value?.getTime?.() || 0);

/**
 * Données du tableau de bord (KPI + files à traiter + derniers arrivés).
 * Regroupe plusieurs lectures légères en une seule requête React Query.
 */
export const useDashboard = () => {
  return useQuery({
    queryKey: [DASHBOARD_KEY],
    queryFn: async () => {
      const [
        clientsTotal,
        sitesData,
        requestsResult,
        ticketCounts,
        openTickets
      ] = await Promise.all([
        getUserCount(),
        getSiteDashboardData(5),
        getAllCreationRequests(),
        getTicketsCountByStatus(),
        getAllTickets({ status: [TICKET_STATUS.NEW, TICKET_STATUS.IN_PROGRESS], limit: 5 })
      ]);

      const requests = Array.isArray(requestsResult?.requests) ? requestsResult.requests : [];

      // Demandes « à traiter » (actives, suivi non terminé), les plus récentes d'abord
      const activeRequests = requests
        .filter((r) => ACTIVE_REQUEST_STATUSES.includes(r.status) && !r.trackingClosed)
        .sort((a, b) => toTime(b.submittedAt || b.updatedAt) - toTime(a.submittedAt || a.updatedAt));

      const newRequestsCount = requests.filter((r) => r.status === 'nouvelle' && !r.trackingClosed).length;
      const openTicketsList = Array.isArray(openTickets) ? openTickets : [];

      return {
        clientsTotal: clientsTotal || 0,
        sitesTotal: sitesData?.total || 0,
        sitesPublished: sitesData?.published || 0,
        recentSites: sitesData?.recentPublished || [],
        requestQueue: activeRequests.slice(0, 5),
        openRequestCount: activeRequests.length,
        newRequestsCount,
        ticketCounts: ticketCounts || {},
        openTicketCount:
          (ticketCounts?.[TICKET_STATUS.NEW] || 0) + (ticketCounts?.[TICKET_STATUS.IN_PROGRESS] || 0),
        openTickets: openTicketsList,
        latestClients: await getLatestClients(5)
      };
    },
    staleTime: 60 * 1000 // 1 min — le dashboard n'a pas besoin d'être temps réel
  });
};
